import { useTranslation } from 'module/hooks/use-translation';
import { NodeViewContent } from '@tiptap/react';
import {
  CustomSectionState,
  RecordBlockCustomSectionAttributes,
} from 'api/custom-section/types';
import { useCallback, useState } from 'react';
import {
  FluidSectionPreview,
  SectionTitle,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { PromptInput } from './prompt-input';
import { RecordBlockPromptType } from '../../../../types/ui-types';
import { RecordBlockSectionComponentProps } from '../types';

export const RecordBlockPromptSection = ({
  node,
  onRemove,
  onDone,
  onUndo,
  contentEditable,
  allowResolve,
  onOpen,
  pageType,
  pageTriggerListFilter,
}: RecordBlockSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;

  const constructPrompt = useCallback(
    (attributes: RecordBlockCustomSectionAttributes) => {
      return {
        aiContext: attributes.config.aiContext,
        title: attributes.title,
        rowCriteria: attributes.config.rowCriteria,
        filterExclusionCriteria: attributes.config.filterExclusionCriteria,
      };
    },
    [],
  );
  const [prompt, setPrompt] = useState<RecordBlockPromptType>(
    constructPrompt(node.attrs),
  );

  const defaultTitleLabel = useTranslation(
    'rich_text_editor.sections.question_section.title',
  );

  const onGenerate = async (userPrompt: RecordBlockPromptType) => {
    const attributes = {
      ...node.attrs,
      title: userPrompt.title,
      state: CustomSectionState.Closed,
      isTableStatic: allowResolve ? true : false,
      config: {
        aiContext: userPrompt.aiContext || '',
        rowCriteria: userPrompt.rowCriteria,
        filterExclusionCriteria: userPrompt.filterExclusionCriteria,
      },
    };

    onDone(attributes, getNodeAsPayload());
  };

  const getNodeAsPayload = useCallback(
    (isPreviewOpen?: boolean) => {
      const payload = node.toJSON();

      if (!('content' in payload)) {
        payload.content = [];
      }

      return {
        ...payload,
        attrs: {
          ...payload.attrs,
          isTableStatic: isPreviewOpen ? true : false,
        },
      };
    },
    [node],
  );

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    setPrompt(constructPrompt(node.attrs));
    onUndo();
  }, [onUndo, node.attrs, constructPrompt, node.content?.size, onRemove]);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <PromptInput
          prompt={prompt}
          setPrompt={setPrompt}
          onGenerate={onGenerate}
          onCancel={() => {
            onRemove();
          }}
          onUndo={onClose}
          allowResolve={allowResolve}
          getNodeAsPayload={getNodeAsPayload}
          pageType={pageType}
          pageTriggerListFilter={pageTriggerListFilter}
        />
      ) : (
        <FluidSectionPreview
          onClick={() => {
            onOpen();
          }}
        >
          <div>
            <SectionTitle>{prompt.title || defaultTitleLabel}</SectionTitle>
          </div>
          <NodeViewContent />
        </FluidSectionPreview>
      )}
    </div>
  );
};
